import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type JSX } from 'react';

import { CertificateApprovalSection } from './CertificateApprovalSection';
import { ColorSwatchField } from './ColorSwatchField';
import { ConfigSection } from './ConfigSection';
import styles from './TenantConfigTab.module.scss';
import {
  updateTenantConfig,
  updatePlatformTenantBranding,
  uploadPlatformTenantLogo,
  uploadPlatformTenantBanner,
  isValidHexColor,
  type TenantBrandingDetail,
  type TenantDetail,
} from '../api/tenants.graphql';

interface Props {
  tenantId: string;
  tenant: TenantDetail;
  onUpdated: () => void;
  /** OS-1717 — sin `platform.tenants.configure` la configuración se ve pero no se cambia. */
  readOnly?: boolean;
}

export function TenantConfigTab({ tenantId, tenant, onUpdated, readOnly = false }: Props): JSX.Element {
  const [flexible, setFlexible] = useState(tenant.flexibleAttendanceCheck ?? false);
  const [primary, setPrimary] = useState(tenant.branding?.colorPrimary ?? '#FF6B35');
  const [secondary, setSecondary] = useState(tenant.branding?.colorSecondary ?? '#004E89');
  const [displayName, setDisplayName] = useState(
    tenant.branding?.displayName ?? tenant.name ?? 'OpsSynk',
  );
  const [logoUrl, setLogoUrl] = useState(tenant.branding?.logoUrl ?? '');
  const [bannerUrl, setBannerUrl] = useState(tenant.branding?.bannerUrl ?? '');
  const [configMsg, setConfigMsg] = useState<string | null>(null);
  const [brandingMsg, setBrandingMsg] = useState<string | null>(null);
  const logoInputId = useId();
  const bannerInputId = useId();
  const logoRef = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFlexible(tenant.flexibleAttendanceCheck ?? false);
    setPrimary(tenant.branding?.colorPrimary ?? '#FF6B35');
    setSecondary(tenant.branding?.colorSecondary ?? '#004E89');
    setDisplayName(tenant.branding?.displayName ?? tenant.name ?? 'OpsSynk');
    setLogoUrl(tenant.branding?.logoUrl ?? '');
    setBannerUrl(tenant.branding?.bannerUrl ?? '');
  }, [tenant]);

  const configMutation = useMutation({
    mutationFn: () => updateTenantConfig(tenantId, { flexibleAttendanceCheck: flexible }),
    onSuccess: () => {
      setConfigMsg('Marcación flexible guardada.');
      onUpdated();
    },
    onError: (err: Error) => setConfigMsg(err.message),
  });

  const brandingMutation = useMutation({
    mutationFn: () => {
      if (!isValidHexColor(primary) || !isValidHexColor(secondary)) {
        throw new Error('Los colores deben ser hex válidos (#RRGGBB)');
      }
      return updatePlatformTenantBranding(tenantId, {
        colorPrimary: primary,
        colorSecondary: secondary,
        displayName: displayName.trim() || tenant.name,
      });
    },
    onSuccess: (branding: TenantBrandingDetail) => {
      setBrandingMsg('Branding guardado.');
      setLogoUrl(branding.logoUrl ?? '');
      setBannerUrl(branding.bannerUrl ?? '');
      onUpdated();
    },
    onError: (err: Error) => setBrandingMsg(err.message),
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => uploadPlatformTenantLogo(tenantId, file),
    onSuccess: (res) => {
      setLogoUrl(res.logoUrl);
      setBrandingMsg('Logo actualizado.');
      onUpdated();
    },
    onError: (err: Error) => setBrandingMsg(err.message),
  });

  const bannerMutation = useMutation({
    mutationFn: (file: File) => uploadPlatformTenantBanner(tenantId, file),
    onSuccess: (res) => {
      setBannerUrl(res.bannerUrl);
      setBrandingMsg('Banner actualizado.');
      onUpdated();
    },
    onError: (err: Error) => setBrandingMsg(err.message),
  });

  return (
    <fieldset className={styles.readOnlyGroup} disabled={readOnly}>
    <div className={styles.stack}>
      {readOnly ? (
        <p className={styles.readOnlyNote}>
          Solo lectura: tu rol no cambia la configuración operativa ni el branding del tenant.
        </p>
      ) : null}
      <ConfigSection
        title="Marcación flexible de asistencia"
        description="Salida anticipada sin PENDING, múltiples entradas/salidas el mismo día, y sin cierre automático de jornada. Por defecto el ciclo es rígido."
        footer={
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={configMutation.isPending}
              onClick={() => {
                setConfigMsg(null);
                configMutation.mutate();
              }}
            >
              {configMutation.isPending ? 'Guardando…' : 'Guardar marcación'}
            </button>
            {configMsg ? <span className={styles.feedback}>{configMsg}</span> : null}
          </>
        }
      >
        <label className={styles.switchRow}>
          <span className={styles.switchCopy}>
            <strong>Activar marcación flexible</strong>
            <span className={styles.hint}>flexibleAttendanceCheck</span>
          </span>
          <span className={styles.switch}>
            <input
              type="checkbox"
              checked={flexible}
              onChange={(e) => setFlexible(e.target.checked)}
            />
            <span className={styles.track} aria-hidden />
          </span>
        </label>
      </ConfigSection>

      <CertificateApprovalSection
        tenantId={tenantId}
        autoApproveLaborCertificates={tenant.autoApproveLaborCertificates ?? false}
        onUpdated={onUpdated}
      />

      <ConfigSection
        title="Branding"
        description="Colores, logo y banner del tenant (Mesero, KDS, Caja y login)."
        footer={
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={brandingMutation.isPending}
              onClick={() => {
                setBrandingMsg(null);
                brandingMutation.mutate();
              }}
            >
              {brandingMutation.isPending ? 'Guardando…' : 'Guardar colores'}
            </button>
            {brandingMsg ? <span className={styles.feedback}>{brandingMsg}</span> : null}
          </>
        }
      >
        <label className={styles.field}>
          Nombre visible
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>

        <div className={styles.colorRow}>
          <ColorSwatchField
            label="Color primario"
            value={primary}
            fallback="#FF6B35"
            onChange={setPrimary}
          />
          <ColorSwatchField
            label="Color secundario"
            value={secondary}
            fallback="#004E89"
            onChange={setSecondary}
          />
        </div>

        <div className={styles.uploadRow}>
          <div className={styles.uploadBlock}>
            <span className={styles.uploadLabel}>Logo</span>
            <span className={styles.hint}>PNG/JPG · máx. 5MB</span>
            <input
              ref={logoRef}
              id={logoInputId}
              type="file"
              accept="image/png,image/jpeg"
              className={styles.hiddenFile}
              // OS-1725 C3 — oculto pero enfocable: se maneja con el botón, así que sale del orden de
              // tabulación y lleva nombre accesible.
              aria-label="Archivo del logo"
              tabIndex={-1}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setBrandingMsg(null);
                  logoMutation.mutate(file);
                }
              }}
            />
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={logoMutation.isPending}
              onClick={() => logoRef.current?.click()}
            >
              {logoMutation.isPending ? 'Subiendo…' : 'Seleccionar logo'}
            </button>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className={styles.preview} />
            ) : null}
          </div>

          <div className={styles.uploadBlock}>
            <span className={styles.uploadLabel}>Banner</span>
            <span className={styles.hint}>PNG/JPG · máx. 10MB</span>
            <input
              ref={bannerRef}
              id={bannerInputId}
              type="file"
              accept="image/png,image/jpeg"
              className={styles.hiddenFile}
              // OS-1725 C3 — oculto pero enfocable: se maneja con el botón, así que sale del orden de
              // tabulación y lleva nombre accesible.
              aria-label="Archivo del banner"
              tabIndex={-1}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setBrandingMsg(null);
                  bannerMutation.mutate(file);
                }
              }}
            />
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={bannerMutation.isPending}
              onClick={() => bannerRef.current?.click()}
            >
              {bannerMutation.isPending ? 'Subiendo…' : 'Seleccionar banner'}
            </button>
            {bannerUrl ? (
              <img src={bannerUrl} alt="Banner" className={styles.previewBanner} />
            ) : null}
          </div>
        </div>
      </ConfigSection>
    </div>
    </fieldset>
  );
}
